// SubscriptionRowLink — the R4 visible row link, shared by the owned-item
// tables and the detail dialog (TD-671 Task 13 moved it out of
// SubscriptionsTable.tsx so OwnedProductsTable can render "Details" without a
// component cycle: SubscriptionsTable already imports OwnedProductsTable).
// SubscriptionsTable re-exports it, so existing imports keep working.
//
// A real <button> (keyboard-reachable, type "button"), styled quiet-but-AA:
// textSecondary at rest (the old `t.colors.placeholder` failed AA on the card
// background at ~2.6:1), underlined on hover AND on keyboard focus — inline
// styles have no :hover/:focus, so the states ride mouse/focus events. Target
// size: the full touchTarget height on a coarse pointer, 24px minimum on fine
// — the touch rule applies to the resolved pointer, not to the token's
// existence.

import { useState } from "react";
import { useEmbedTokens } from "../../embed-shared/primitives";

export function SubscriptionRowLink({
  label,
  ariaLabel,
  loadingLabel,
  loading = false,
  disabled = false,
  onClick,
}: {
  label: string;
  /** Distinguishes identical links across rows (e.g. "Details for Pro Suite"). */
  ariaLabel?: string;
  /** In-flight copy, e.g. "Resuming..." — unchanged from the old control. */
  loadingLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Receives the clicked element so a dialog can return focus to it. */
  onClick: (trigger: HTMLElement) => void;
}) {
  const t = useEmbedTokens();
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled || loading}
      onClick={(e) => onClick(e.currentTarget)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{
        fontFamily: "inherit",
        fontSize: t.fontSize.xs,
        fontWeight: t.fontWeight.regular,
        color: t.colors.textSecondary,
        background: "transparent",
        border: 0,
        padding: "0 4px",
        display: "inline-flex",
        alignItems: "center",
        textDecoration: hover || focus ? "underline" : "none",
        cursor: disabled || loading ? "default" : "pointer",
        minHeight: t.pointer === "coarse" ? t.size.touchTarget : 24,
      }}
    >
      {loading && loadingLabel ? loadingLabel : label}
    </button>
  );
}
